
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { Search, CalendarRange } from 'lucide-react';

interface PatientFilterCardProps {
  searchQuery?: string;
  setSearchQuery?: (value: string) => void;
  selectedStatus?: string;
  setSelectedStatus?: (value: string) => void;
  date?: DateRange | undefined;
  setDate?: (value: DateRange | undefined) => void;
  isFilterActive?: boolean;
  handleResetFilters?: () => void;
  onFilter?: () => void;
}

const PatientFilterCard: React.FC<PatientFilterCardProps> = ({
  searchQuery = '',
  setSearchQuery = () => {},
  selectedStatus = 'all',
  setSelectedStatus = () => {},
  date,
  setDate = () => {},
  isFilterActive = false,
  handleResetFilters = () => {},
  onFilter = () => {}
}) => {
  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Filter Patients</CardTitle>
        <CardDescription>
          Use the filters below to find specific patients
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col md:flex-row gap-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search patients..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <Select 
            value={selectedStatus} 
            onValueChange={setSelectedStatus}
          >
            <SelectTrigger>
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cases</SelectItem>
              <SelectItem value="new">New Cases</SelectItem>
              <SelectItem value="in-treatment">In Treatment</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="justify-start text-left font-normal"
              >
                <CalendarRange className="mr-2 h-4 w-4" />
                {date?.from ? (
                  date.to ? (
                    <>
                      {format(date.from, "LLL dd, y")} -{" "}
                      {format(date.to, "LLL dd, y")}
                    </>
                  ) : (
                    format(date.from, "LLL dd, y")
                  )
                ) : (
                  <span>Date Range</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={date?.from}
                selected={date}
                onSelect={setDate}
                numberOfMonths={2}
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </div>
        
        {isFilterActive && (
          <Button 
            variant="outline" 
            onClick={handleResetFilters}
            className="shrink-0"
          >
            Reset Filters
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default PatientFilterCard;
